import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { SyncButton } from '@/components_deprecated/Button'
import { ModuleTitle } from '@/components_deprecated/Title/ModuleTitle'
import { formatISO } from 'date-fns'
import { ALL_USER_EXPORTS_QUERY } from '@/modules/exports/queries'
import { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { useInfiniteScroll, useNodes } from '@/utils/hooks'
import { useBreadcrumbs } from '@/utils/hooks/useBreadcrumbs'
import { DEFAULT_PAGE_SIZE } from '@/utils/pagination'
import { UserExportsTable } from './UserExportsTable'
import { useInfoModal } from '@/utils/hooks/useInfoModal/useInfoModal'
import { PageType } from '@/modules/common/consts'

const UserExportsWrapper = styled.div`
  ${props => props.theme.layout.default};
`

const TitleWrapper = styled.span`
  display: flex;
  & > * + * {
    margin-left: 1rem;
  }
  align-items: center;
  justify-content: space-between;
`

interface UserExportsProps {}
export const UserExports: React.FC<UserExportsProps> = () => {
  const translations = useTranslate({
    userExports: 'exports.user-exports',
  })

  const [lastVisit, setLastVisit] = useState<string>()

  const { setOverrides, overrides } = useBreadcrumbs()

  const { InfoModal, infoContent, InfoButton } = useInfoModal(PageType.EXPORTS)

  const {
    data: userExportsData,
    loading: userExportsLoading,
    refetch: refetchUserExports,
    fetchMore: fetchMoreUserExports,
  } = useQuery(ALL_USER_EXPORTS_QUERY, {
    variables: {
      first: DEFAULT_PAGE_SIZE,
      orderBy: '-createdAt',
    },
    pollInterval: 2500,
    // We need this for the sync animation to trigger
    notifyOnNetworkStatusChange: true,
    onCompleted() {
      setLastVisit(
        lastVisit ?? (localStorage.getItem('userExportsLastVisit') || '')
      )
      localStorage.setItem('userExportsLastVisit', formatISO(new Date()))
    },
  })

  const userExports = useNodes(userExportsData?.allUserExports)
  const pageInfo = userExportsData?.allUserExports.pageInfo
  const isFirstLoad = !userExportsData

  const handleScrollBottom = useCallback(async () => {
    if (
      typeof userExportsData === 'undefined' ||
      typeof pageInfo === 'undefined'
    )
      return
    if (!pageInfo.hasNextPage) return
    if (!pageInfo.endCursor) return
    try {
      await fetchMoreUserExports({
        variables: {
          first: DEFAULT_PAGE_SIZE,
          orderBy: '-createdAt',
          after: pageInfo.endCursor,
        },
      })
    } catch (e) {
      console.error(e)
    }
  }, [userExportsData, fetchMoreUserExports, pageInfo])

  useInfiniteScroll(
    handleScrollBottom,
    300,
    !userExportsLoading && pageInfo?.hasNextPage
  )

  useEffect(() => {
    if (overrides['user-exports']) return
    setOverrides({ 'user-exports': translations.userExports })
  }, [overrides, setOverrides, translations.userExports])

  function dateifyLastVisit() {
    const date = new Date(lastVisit ?? '')
    return isNaN(date.getTime()) ? new Date() : date
  }

  return (
    <UserExportsWrapper>
      <TitleWrapper>
        <ModuleTitle>
          {translations.userExports}
          {infoContent && <InfoButton />}
        </ModuleTitle>

        <SyncButton
          background="white"
          reset={userExportsLoading}
          refetchQuery={refetchUserExports}
        />
      </TitleWrapper>

      <UserExportsTable
        userExports={userExports}
        loading={userExportsLoading}
        isFirstLoad={isFirstLoad}
        lastVisit={dateifyLastVisit()}
      />
      {infoContent && <InfoModal />}
    </UserExportsWrapper>
  )
}
